import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Button,
  Loader,
  Menu,
  Popover,
  SegmentedControl,
  Select,
  Text,
  TextInput,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import {
  IconBell,
  IconChevronDown,
  IconChevronRight,
  IconDotsVertical,
  IconPencil,
  IconPlus,
  IconSearch,
  IconSparkles,
  IconTrash,
} from '@tabler/icons-react'
import {
  checklistCounts,
  useAssignees,
  useBoard,
  useBoardMutations,
  type BoardItem,
} from '@/pages/Boards/hooks'
import { ItemDrawer } from '@/pages/Boards/ItemDrawer'
import { openRenameModal } from '@/pages/Boards/renameModal'
import { openDeleteBoardModal } from '@/pages/Boards/deleteBoardModal'
import { QueueTable } from './QueueTable'
import { QueueStats } from './QueueStats'
import { StickyScrollArea } from './StickyScrollArea'
import { BulkActionBar } from './BulkActionBar'
import { CareTaskSettingsModal } from './CareTaskSettingsModal'
import { BoardNotificationSettingsModal } from './BoardNotificationSettingsModal'
import { COMPLETED_GROUP_LABEL } from './groups'
import { readDensity, writeDensity } from './lastBoardStorage'
import { QueueFilterPanel } from './QueueFilterPanel'
import { MONO, QUEUE_COLOR, QUEUE_LEGEND, SANS, type QueueDensity } from './queueTheme'
import {
  activeFilterCount,
  applyQueueFiltersAndSort,
  DEFAULT_QUEUE_FILTERS,
  DEFAULT_QUEUE_SORT,
  latestNoteTime,
  type QueueColumnIds,
  type QueueFilters,
  type QueueSort,
} from './queueFilters'

// Toolbar controls all wear the design's chip: a hairline border on an almost-white
// ground, 7px radius, one shared height. Passed through Mantine's own props and
// `styles` rather than Tailwind classes — Mantine's component CSS is unlayered and
// would outrank the utilities (see queueTheme).
const CHIP_SIZE = 'sm' as const
const CHIP_RADIUS = 7
const chipStyles = {
  root: {
    background: '#fbfcfd',
    borderColor: QUEUE_COLOR.border,
    color: QUEUE_COLOR.ink,
    fontWeight: 500,
    fontSize: 13.5,
  },
}

// Everything searchable about an item: its name plus every cell value.
const itemText = (item: BoardItem): string =>
  [
    item.name,
    ...Object.values(item.values).map((value) =>
      typeof value === 'object' && value !== null ? value.display : (value ?? ''),
    ),
  ]
    .join(' ')
    .toLowerCase()

// Stable empty read map so a not-yet-loaded board doesn't churn effect deps.
const EMPTY_READ_MAP: Record<string, string> = {}

const AddBoardControl = ({
  label,
  onCreated,
}: {
  label: string
  onCreated: (boardId: string) => void
}) => {
  const [opened, setOpened] = useState(false)
  const [name, setName] = useState('')
  const { createBoard } = useBoardMutations()

  const submit = async () => {
    const trimmed = name.trim()
    if (!trimmed || createBoard.isPending) return
    try {
      const result = await createBoard.mutateAsync({ name: trimmed })
      setName('')
      setOpened(false)
      onCreated(result.board.id)
    } catch {
      // Error notification handled by the mutation hook.
    }
  }

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" shadow="md" withinPortal>
      <Popover.Target>
        <Button
          variant="default"
          size={CHIP_SIZE}
          radius={CHIP_RADIUS}
          styles={chipStyles}
          leftSection={<IconPlus size={14} />}
          onClick={() => setOpened((prev) => !prev)}
        >
          {label}
        </Button>
      </Popover.Target>
      <Popover.Dropdown className="w-56">
        <TextInput
          autoFocus
          size="xs"
          label="New board"
          placeholder="Board name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit()
            if (e.key === 'Escape') setOpened(false)
          }}
        />
        <Button
          size="xs"
          fullWidth
          className="mt-2"
          loading={createBoard.isPending}
          disabled={!name.trim()}
          onClick={() => void submit()}
        >
          Create
        </Button>
      </Popover.Dropdown>
    </Popover>
  )
}

type CareQueueTabProps = {
  boardId: string | undefined
  boards: { id: string; name: string }[]
  onSelectBoard: (boardId: string) => void
  // A task to open on load (from a notification deep link: ?item=<itemId>).
  initialItemId?: string | null
}

export const CareQueueTab = ({
  boardId,
  boards,
  onSelectBoard,
  initialItemId,
}: CareQueueTabProps) => {
  const { data, isLoading, isError, refetch } = useBoard(boardId)
  const { renameBoard, deleteBoard, bulkUpdateItems, bulkDeleteItems, markRead } =
    useBoardMutations(boardId)
  const medplum = useMedplum()
  const { data: assigneeData } = useAssignees()
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<QueueFilters>(DEFAULT_QUEUE_FILTERS)
  const [sort, setSort] = useState<QueueSort>(DEFAULT_QUEUE_SORT)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // "Me" = the logged-in profile, matched against the board's assignee column.
  const profile = medplum.getProfile()
  const myReference = profile?.id ? `${profile.resourceType}/${profile.id}` : undefined
  // Per-user read map from the server (task id → newest note time seen).
  const readAt = data?.readAt ?? EMPTY_READ_MAP
  const personColumnId = data?.board.columns.find((c) => c.type === 'person')?.id
  // Cell values are keyed by column id; resolve the built-in columns' ids rather
  // than assuming they equal their type, so filters and stats stay correct.
  const statusColumnId = data?.board.columns.find((c) => c.type === 'status')?.id
  const priorityColumnId = data?.board.columns.find((c) => c.type === 'priority')?.id
  const dateColumnId = data?.board.columns.find((c) => c.type === 'date')?.id

  const [completedCollapsed, setCompletedCollapsed] = useState(false)

  // Row density is a per-user preference, restored the same way the last board
  // is. Same read-during-render assumption: the profile is available by the time
  // this tab renders inside the authenticated shell.
  const userId = profile?.id
  const [density, setDensity] = useState<QueueDensity>(() =>
    userId ? readDensity(userId) : 'roomy',
  )
  const changeDensity = (next: QueueDensity) => {
    setDensity(next)
    if (userId) writeDensity(userId, next)
  }

  const completedGroup = data?.board.groups.find((g) => g.label === COMPLETED_GROUP_LABEL)
  const group = data?.board.groups.find((g) => g.label !== COMPLETED_GROUP_LABEL)

  // The queue tab also shows the board's Completed section below the queue, so
  // Done items land in view instead of vanishing to the other tab. The stat
  // cards use the unfiltered lists — they're KPIs, not search results.
  const { items, completedItems, allQueue, allCompleted } = useMemo(() => {
    const cols: QueueColumnIds = {
      status: statusColumnId,
      priority: priorityColumnId,
      person: personColumnId,
      date: dateColumnId,
    }
    const inGroup = (groupId?: string) =>
      data?.items.filter((item) => item.groupId === groupId) ?? []
    const applyFilters = (list: BoardItem[]) => {
      let filtered = list
      const query = search.trim().toLowerCase()
      if (query) filtered = filtered.filter((item) => itemText(item).includes(query))
      return applyQueueFiltersAndSort(filtered, filters, sort, cols, myReference)
    }
    const queueAll = inGroup(group?.id)
    const completedAll = inGroup(completedGroup?.id)
    return {
      items: applyFilters(queueAll),
      completedItems: applyFilters(completedAll),
      allQueue: queueAll,
      allCompleted: completedAll,
    }
  }, [
    data,
    group,
    completedGroup,
    search,
    filters,
    sort,
    myReference,
    personColumnId,
    statusColumnId,
    priorityColumnId,
    dateColumnId,
  ])

  // Drop selections that no longer exist (deleted, or we switched boards) so the
  // action-bar count stays truthful.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev
      const existing = new Set(data?.items.map((item) => item.id))
      const next = new Set([...prev].filter((id) => existing.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [data])

  // Mark a task's updates read for this user (server-side) while it's open — on
  // open, and again if a newer note lands while the drawer stays open (e.g. the
  // viewer posts one, so their own update never leaves the task flagged unread).
  // The ref pins this to one call per (task, newest-note) state, so re-renders
  // can't turn it into a request loop.
  const markedRef = useRef('')
  useEffect(() => {
    if (!selectedItemId) return
    const item = data?.items.find((i) => i.id === selectedItemId)
    const latest = item ? latestNoteTime(item) : null
    if (!latest) return
    const stateKey = `${selectedItemId} ${latest}`
    if (markedRef.current === stateKey) return
    markedRef.current = stateKey
    if ((readAt[selectedItemId] ?? '') < latest) {
      void markRead.mutateAsync({ itemId: selectedItemId, seenAt: latest }).catch(() => undefined)
    }
  }, [selectedItemId, data, readAt, markRead])

  // Open a task requested via a notification deep link (?item=…) once its board
  // has loaded. Removing the deep link closes that drawer (the active-tab reset);
  // the ref still prevents a later refetch from reopening a drawer the user closed.
  const openedInitialRef = useRef<string | null>(null)
  useEffect(() => {
    if (!initialItemId) {
      if (openedInitialRef.current) {
        openedInitialRef.current = null
        setSelectedItemId(null)
      }
      return
    }
    if (openedInitialRef.current === initialItemId) return
    if (data?.items.some((item) => item.id === initialItemId)) {
      openedInitialRef.current = initialItemId
      setSelectedItemId(initialItemId)
    }
  }, [initialItemId, data])

  // A task has unread updates when its newest note/reply is newer than the last
  // time this user opened it (per-user read state, persisted server-side).
  const isUnread = (item: BoardItem): boolean => {
    const latest = latestNoteTime(item)
    return !!latest && latest > (readAt[item.id] ?? '')
  }

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleSelectAll = (ids: string[], select: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)))
      return next
    })

  const clearSelection = () => setSelectedIds(new Set())

  // Run a bulk mutation over the current selection, then clear it.
  const runBulk = (run: () => Promise<unknown>) =>
    void run()
      .then(clearSelection)
      .catch(() => undefined)

  const applyBulkStatus = (status: string) => {
    if (!statusColumnId) return
    let itemIds = [...selectedIds]

    // A fax-linked task can only be completed with a completion note, which the
    // server enforces — a bulk Done over one fails with a bare "N of M tasks
    // could not be updated". Skip those instead and say why, so the rest of the
    // selection still moves and the user knows where to finish the remainder.
    // One shared note across many tasks would be worse than none, so we don't
    // prompt for one here.
    if (status === 'Done') {
      const needsNote = new Set(
        (data?.items ?? [])
          .filter((i) => i.sourceFaxDocumentReferenceId && !i.completion)
          .map((i) => i.id),
      )
      const skipped = itemIds.filter((id) => needsNote.has(id))
      itemIds = itemIds.filter((id) => !needsNote.has(id))
      if (skipped.length) {
        showNotification({
          color: 'yellow',
          title: `${skipped.length} fax ${skipped.length === 1 ? 'task' : 'tasks'} skipped`,
          message:
            'Fax tasks need a completion note. Set their status to Done individually to add one.',
        })
      }
      if (!itemIds.length) {
        clearSelection()
        return
      }
    }

    runBulk(() =>
      bulkUpdateItems.mutateAsync({
        itemIds,
        values: { [statusColumnId]: status },
      }),
    )
  }

  const applyBulkPriority = (priority: string) => {
    if (!priorityColumnId) return
    runBulk(() =>
      bulkUpdateItems.mutateAsync({
        itemIds: [...selectedIds],
        values: { [priorityColumnId]: priority },
      }),
    )
  }

  const confirmBulkDelete = () => {
    const ids = [...selectedIds]
    modals.openConfirmModal({
      title: 'Delete tasks',
      children: (
        <Text size="sm">
          Delete {ids.length} selected {ids.length === 1 ? 'task' : 'tasks'}? Their updates will be
          deleted too.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => runBulk(() => bulkDeleteItems.mutateAsync(ids)),
    })
  }

  if (!boardId) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-[#EEEEEE] bg-white py-16">
        <p className="text-sm text-gray-500">No care queue yet</p>
        <AddBoardControl label="Create care queue" onCreated={onSelectBoard} />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader size="sm" />
      </div>
    )
  }

  // A stale ?board=<deleted-id> (or a transient read failure) resolves the query
  // to an error with no data — recover instead of spinning forever.
  if (isError || !data || !group) {
    const fallback = boards.find((b) => b.id !== boardId)
    return (
      <div className="flex flex-col items-center gap-3 rounded-md border border-[#EEEEEE] bg-white py-16">
        <p className="text-sm text-gray-500">This care queue couldn’t be loaded.</p>
        <div className="flex gap-2">
          <Button size="xs" variant="default" onClick={() => void refetch()}>
            Try again
          </Button>
          {fallback && (
            <Button size="xs" onClick={() => onSelectBoard(fallback.id)}>
              Go to {fallback.name}
            </Button>
          )}
        </div>
      </div>
    )
  }

  const selectedItem = data.items.find((item) => item.id === selectedItemId) ?? null
  // Steps only earn a column on boards that actually use checklists. Decided
  // across the unfiltered lists so the column — and therefore both tables'
  // widths — can't shift as someone types in the search box.
  const showSteps = [...allQueue, ...allCompleted].some((item) => checklistCounts(item).total > 0)
  const hasActiveFilter =
    !!search.trim() ||
    activeFilterCount(filters, {
      status: !!statusColumnId,
      priority: !!priorityColumnId,
      assignee: !!personColumnId,
      dueDate: !!dateColumnId,
    }) > 0

  const renameBoardPrompt = () =>
    openRenameModal({
      title: 'Rename board',
      label: 'Board name',
      initial: data.board.name,
      onSubmit: (name) =>
        void renameBoard.mutateAsync({ id: data.board.id, name }).catch(() => undefined),
    })

  const openCareTaskSettings = () =>
    modals.open({
      title: 'Care task automation',
      children: <CareTaskSettingsModal />,
    })

  const openNotificationSettings = () =>
    modals.open({
      title: 'My notifications',
      size: 'lg',
      children: <BoardNotificationSettingsModal />,
    })

  const confirmDeleteBoard = () =>
    openDeleteBoardModal({
      name: data.board.name,
      onConfirm: () => void deleteBoard.mutateAsync({ id: data.board.id }).catch(() => undefined),
    })

  return (
    // pb clears the fixed bulk-action bar (BulkActionBar), the sticky horizontal
    // scrollbar, and the "add task" bar while it's pinned (QueueTable), so the last
    // row can scroll out from behind them.
    <div className="flex flex-col gap-4 pb-24" style={{ fontFamily: SANS }}>
      <QueueStats
        todoItems={allQueue}
        completedItems={allCompleted}
        priorityColumnId={priorityColumnId}
        dateColumnId={dateColumnId}
      />

      {/* The board card. The toolbar lives INSIDE it, as the design draws it, but
          OUTSIDE StickyScrollArea — parked in the scroller it would slide out of
          reach sideways whenever the table is wider than the card. */}
      <div
        className="overflow-hidden rounded-[14px] bg-white"
        style={{
          border: `1px solid ${QUEUE_COLOR.border}`,
          boxShadow: '0 1px 2px rgba(27,36,48,0.04), 0 12px 32px -20px rgba(27,36,48,0.28)',
        }}
      >
        <div
          className="flex flex-wrap items-center gap-3 px-8 py-5"
          style={{ borderBottom: `1px solid ${QUEUE_COLOR.hairline}` }}
        >
          <Select
            size={CHIP_SIZE}
            radius={CHIP_RADIUS}
            // Wide enough to show a board's full name rather than truncating it.
            w={280}
            aria-label="Care queue board"
            data={boards.map((b) => ({ value: b.id, label: b.name }))}
            value={boardId}
            onChange={(id) => id && onSelectBoard(id)}
            allowDeselect={false}
          />
          <AddBoardControl label="New board" onCreated={onSelectBoard} />
          <Menu shadow="md" position="bottom-start" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" aria-label="Board actions">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {data.canManage && (
                <Menu.Item leftSection={<IconPencil size={14} />} onClick={renameBoardPrompt}>
                  Rename board
                </Menu.Item>
              )}
              <Menu.Item leftSection={<IconSparkles size={14} />} onClick={openCareTaskSettings}>
                Care task automation
              </Menu.Item>
              <Menu.Item leftSection={<IconBell size={14} />} onClick={openNotificationSettings}>
                My notifications
              </Menu.Item>
              {data.canManage && (
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={confirmDeleteBoard}
                >
                  Delete board
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
          <div className="flex-1" />
          <SegmentedControl
            size="xs"
            radius={CHIP_RADIUS}
            aria-label="Row density"
            value={density}
            onChange={(value) => changeDensity(value as QueueDensity)}
            data={[
              { value: 'roomy', label: 'Roomy' },
              { value: 'compact', label: 'Compact' },
            ]}
          />
          <TextInput
            size={CHIP_SIZE}
            radius={CHIP_RADIUS}
            w={220}
            placeholder="Search tasks…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <QueueFilterPanel
            filters={filters}
            sort={sort}
            assignees={assigneeData?.assignees ?? []}
            columns={{
              dueDate: !!dateColumnId,
              status: !!statusColumnId,
              priority: !!priorityColumnId,
              assignee: !!personColumnId,
            }}
            onFiltersChange={setFilters}
            onSortChange={setSort}
          />
        </div>

        {/* One scroll surface for both tables, driven by a scrollbar fixed to the
            bottom of the screen. Both share it so their columns line up, and
            Completed reads as a demoted band of the same board rather than a
            second, competing table. */}
        <StickyScrollArea>
          <QueueTable
            board={data.board}
            groupId={group.id}
            patientPhones={data.patientPhones}
            items={items}
            onOpenItem={setSelectedItemId}
            allowAdd
            emptyMessage={
              hasActiveFilter ? 'No tasks match the current filters' : 'No tasks in the queue'
            }
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleSelectAll={toggleSelectAll}
            isUnread={isUnread}
            density={density}
            showSteps={showSteps}
          />

          {completedGroup && (
            <section style={{ background: QUEUE_COLOR.surfaceAlt }}>
              <button
                type="button"
                onClick={() => setCompletedCollapsed((prev) => !prev)}
                aria-expanded={!completedCollapsed}
                className="flex w-full cursor-pointer items-center gap-3 px-8 py-4 text-left"
                style={{ borderTop: `1px solid ${QUEUE_COLOR.border}` }}
              >
                {completedCollapsed ? (
                  <IconChevronRight size={15} style={{ color: QUEUE_COLOR.muted }} />
                ) : (
                  <IconChevronDown size={15} style={{ color: QUEUE_COLOR.muted }} />
                )}
                <span
                  className="h-[7px] w-[7px] rounded-full"
                  style={{ background: QUEUE_COLOR.done }}
                />
                <span className="text-[13.5px] font-semibold" style={{ color: QUEUE_COLOR.ink }}>
                  {completedGroup.label}
                </span>
                <span
                  className="text-[11.5px]"
                  style={{ fontFamily: MONO, color: QUEUE_COLOR.muted }}
                >
                  {completedItems.length} {completedItems.length === 1 ? 'item' : 'items'}
                </span>
              </button>
              {!completedCollapsed && (
                <QueueTable
                  board={data.board}
                  groupId={completedGroup.id}
                  patientPhones={data.patientPhones}
                  items={completedItems}
                  onOpenItem={setSelectedItemId}
                  allowAdd={false}
                  emptyMessage={
                    hasActiveFilter
                      ? 'No completed tasks match the current filters'
                      : 'No completed tasks yet'
                  }
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  onToggleSelectAll={toggleSelectAll}
                  isUnread={isUnread}
                  density={density}
                  showSteps={showSteps}
                  quiet
                />
              )}
            </section>
          )}
        </StickyScrollArea>
      </div>

      {/* Colour key, mirroring the one under the Care Journeys board. */}
      <div className="flex flex-wrap items-center gap-6 px-1">
        {QUEUE_LEGEND.map((entry) => (
          <div key={entry.label} className="flex items-center gap-2">
            <div className="h-[7px] w-[7px] rounded-full" style={{ background: entry.color }} />
            <div className="text-[12px]" style={{ color: QUEUE_COLOR.subtle }}>
              {entry.label}
            </div>
          </div>
        ))}
      </div>

      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          canSetStatus={!!statusColumnId}
          canSetPriority={!!priorityColumnId}
          pending={bulkUpdateItems.isPending || bulkDeleteItems.isPending}
          onSetStatus={applyBulkStatus}
          onSetPriority={applyBulkPriority}
          onDelete={confirmBulkDelete}
          onClear={clearSelection}
        />
      )}

      <ItemDrawer item={selectedItem} onClose={() => setSelectedItemId(null)} />
    </div>
  )
}
